import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  FileText, Globe2, TrendingUp, Clock, CheckCircle2, Film, Coins, Target,
  Calendar, Lock, CheckSquare, Zap, Award, Users, Timer, Trophy, BarChart3,
  ClipboardList, Check, Loader2, AlertCircle, FileQuestion, ArrowRight, Activity, FolderOpen,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr || dateStr === '-' || dateStr === '') return null;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  try {
    let day, month, year;
    if (dateStr.includes('-') && dateStr.length >= 10) {
      const parts = dateStr.split(' ')[0].split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        year = parts[0]; day = parseInt(parts[1]); month = parseInt(parts[2]) - 1;
      }
    } else if (dateStr.includes('/')) {
      const parts = dateStr.split(/[/,\s]/);
      if (parts.length >= 3) { day = parseInt(parts[0]); month = parseInt(parts[1]) - 1; year = parts[2]; }
    }
    if (day && month >= 0 && month < 12 && year) return `${day} ${months[month]} ${year}`;
    return dateStr;
  } catch { return null; }
};

const TranslatorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeView = searchParams.get("view") || "home";

  const [earnings, setEarnings] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [portalSettings, setPortalSettings] = useState(null);
  const [lockedBatchName, setLockedBatchName] = useState(null);
  const [projectsByMonth, setProjectsByMonth] = useState(null);
  const [lockedInvoices, setLockedInvoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [confirmingInvoice, setConfirmingInvoice] = useState(null);
  const [pendencyStatus, setPendencyStatus] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [earningsRes, tasksRes, batchRes, analyticsRes, settingsRes, invoiceRes, assignRes, projMonthRes, lockedInvRes] = await Promise.all([
        axios.get(`${API}/translator/my-earnings`),
        axios.get(`${API}/translator/my-tasks`),
        axios.get(`${API}/batches/locked`),
        axios.get(`${API}/translator/my-analytics`),
        axios.get(`${API}/portal-settings/public`),
        axios.get(`${API}/translator/my-invoices`),
        axios.get(`${API}/translator/my-assignments`),
        axios.get(`${API}/translator/my-projects-by-month`),
        axios.get(`${API}/translator/my-locked-invoices`),
      ]);
      setEarnings(earningsRes.data);
      setTasks(tasksRes.data);
      setAnalytics(analyticsRes.data);
      setPortalSettings(settingsRes.data.settings || null);
      if (batchRes.data.batch) setLockedBatchName(batchRes.data.batch.name);

      const invs = invoiceRes.data.invoices || [];
      setInvoices(invs);
      const initSelected = {};
      invs.forEach(inv => {
        if (inv.confirmed_line_items?.length > 0) initSelected[inv.id] = new Set(inv.confirmed_line_items);
      });
      setSelectedItems(initSelected);

      setMyAssignments(assignRes.data.assignments || []);
      setProjectsByMonth(projMonthRes.data);
      setLockedInvoices(lockedInvRes.data);

      // Fetch pendency status
      try {
        const pendRes = await axios.get(`${API}/translator/pendency-status`);
        setPendencyStatus(pendRes.data);
      } catch { /* ignore */ }
    } catch (error) {
      toast.error("Failed to load data");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Task confirmation
  const handleConfirmTask = async (taskId) => {
    setConfirmingId(taskId);
    try {
      await axios.post(`${API}/translator/tasks/confirm`, { task_id: taskId });
      toast.success("Task confirmed");
      setTasks(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.id === taskId ? { ...t, translator_confirmed: true } : t),
      }));
    } catch (e) { toast.error("Failed to confirm task"); }
    finally { setConfirmingId(null); }
  };

  const handleConfirmAllTasks = async () => {
    setConfirmingId("all");
    try {
      const res = await axios.post(`${API}/translator/tasks/confirm-bulk`);
      const data = res.data;
      toast.success(`${data.confirmed_count} tasks confirmed`);
      setTasks(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => ({ ...t, translator_confirmed: true })),
      }));

      if (data.invoice_generated) {
        toast.success(`Invoice ${data.invoice_number} generated and emailed to you!`, { duration: 6000 });
        fetchData(); // Refresh to get the new invoice
      } else if (data.pending_claims > 0) {
        toast.warning(data.invoice_blocked_reason, { duration: 8000 });
      }

      // Refresh pendency status
      try {
        const pendRes = await axios.get(`${API}/translator/pendency-status`);
        setPendencyStatus(pendRes.data);
      } catch { /* ignore */ }
    } catch (e) { toast.error("Failed to confirm tasks"); }
    finally { setConfirmingId(null); }
  };

  const handleConfirmAssignment = async (assignId) => {
    setConfirmingId(assignId);
    try {
      await axios.post(`${API}/translator/assignments/confirm`, { task_id: assignId });
      toast.success("Assignment confirmed");
      setMyAssignments(prev => prev.map(a => a.id === assignId ? { ...a, translator_confirmed: true } : a));
    } catch (e) { toast.error("Failed to confirm assignment"); }
    finally { setConfirmingId(null); }
  };

  // Invoice handlers
  const handleApproveAll = async (invoiceId) => {
    setConfirmingInvoice(invoiceId);
    try {
      await axios.post(`${API}/translator/invoices/${invoiceId}/approve-all`);
      toast.success("All items approved");
      fetchData();
    } catch (e) { toast.error("Failed to approve"); }
    finally { setConfirmingInvoice(null); }
  };

  // Translator status update
  const handleUpdateTranslatorStatus = async (taskId, status, issueNote = null) => {
    try {
      await axios.put(`${API}/translator/tasks/${taskId}/translator-status`, {
        translator_status: status,
        issue_note: issueNote,
      });
      toast.success(`Status updated to ${status.replace("_", " ")}`);
      // Update local state
      setProjectsByMonth(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          months: prev.months.map(m => ({
            ...m,
            tasks: m.tasks.map(t => t.id === taskId ? { ...t, translator_status: status } : t),
          })),
        };
      });
    } catch { toast.error("Failed to update status"); }
  };

  const handleDispute = async (invoiceId) => {
    setConfirmingInvoice(invoiceId);
    try {
      await axios.post(`${API}/translator/invoices/${invoiceId}/dispute`);
      toast.info("Invoice disputed — admin will review");
      fetchData();
    } catch (e) { toast.error("Failed to dispute"); }
    finally { setConfirmingInvoice(null); }
  };

  const handleApproveInvoice = async (batchId) => {
    try {
      await axios.post(`${API}/translator/invoices/${batchId}/approve-invoice`);
      toast.success("Invoice approved");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve invoice"); }
  };

  const handleDisputeInvoice = async (batchId, message) => {
    try {
      await axios.post(`${API}/translator/invoices/${batchId}/dispute`, { message });
      toast.success("Dispute submitted — admin will review");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit dispute"); }
  };

  const totalTasks = tasks?.total_tasks || 0;
  const totalEarned = (earnings?.total_earned || 0) + (tasks?.assignment_earnings?.total_amount || 0);
  const currentMonthLocked = lockedInvoices?.current_month_locked_amount || 0;
  const unconfirmedTasks = (tasks?.tasks || []).filter(t => !t.translator_confirmed).length;
  const unconfirmedAssignments = myAssignments.filter(a => !a.translator_confirmed).length;
  const sections = portalSettings?.sections || {};
  const isOn = (key) => sections[key] !== false;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="translator-dashboard">
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Poppins, system-ui' }}>
              {activeView === "home" && `Welcome, ${earnings?.display_name || user?.display_name || 'Translator'}`}
              {activeView === "analytics" && "Analytics"}
              {activeView === "projects" && "Projects Assigned"}
              {activeView === "assignments" && "Mini Assignments"}
              {activeView === "earnings" && "Earnings"}
              {activeView === "locked" && "Invoices Locked"}
              {activeView === "invoices" && "Invoices Generated"}
              {activeView === "profile" && "My Profile"}
            </h1>
            <p className="text-xs text-slate-400">
              {activeView === "home" ? "Your personalized dashboard" : `Welcome, ${earnings?.display_name || user?.display_name || 'Translator'}`}
              {lockedBatchName && <span className="ml-2 text-teal-600 font-medium">Batch: {lockedBatchName}</span>}
            </p>
          </div>
          <div className="hidden md:flex items-center gap-4 text-right">
            {activeView !== "home" && (<>
            <div>
              <p className="text-[10px] text-slate-400 uppercase">Tasks</p>
              <p className="text-sm font-bold text-slate-800">{totalTasks}</p>
            </div>
            <div className="w-px h-8 bg-slate-200" />
            <div>
              <p className="text-[10px] text-slate-400 uppercase">Earned</p>
              <p className="text-sm font-bold text-emerald-600">{"\u20B9"}{totalEarned.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            </>)}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Missing Project Banner */}
        {activeView !== "analytics" && activeView !== "home" && isOn("show_missing_banner") && (
          <div
            className="mb-5 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => navigate("/portal/missing-project")}
            data-testid="missing-project-banner"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Is something missing from your projects?</p>
                <p className="text-xs text-amber-600">Click here to report missing projects and get them added</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-500" />
          </div>
        )}

        {/* === HOME VIEW === */}
        {activeView === "home" && (
          <HomeView
            earnings={earnings}
            tasks={tasks}
            myAssignments={myAssignments}
            user={user}
            navigate={navigate}
            pendencyStatus={pendencyStatus}
            sections={sections}
            currentMonthLocked={currentMonthLocked}
            lockedInvoices={lockedInvoices}
            projectsByMonth={projectsByMonth}
            handleUpdateTranslatorStatus={handleUpdateTranslatorStatus}
          />
        )}

        {/* === ANALYTICS VIEW === */}
        {activeView === "analytics" && analytics && (
          <AnalyticsView analytics={analytics} tasks={tasks} earnings={earnings} myAssignments={myAssignments} sections={sections} />
        )}

        {/* === PROJECTS VIEW === */}
        {activeView === "projects" && (
          <ProjectsView
            projectsByMonth={projectsByMonth}
            tasks={tasks}
            onStatusUpdate={handleUpdateTranslatorStatus}
            pendencyStatus={pendencyStatus}
            navigate={navigate}
            sections={sections}
          />
        )}

        {/* === ASSIGNMENTS VIEW === */}
        {activeView === "assignments" && (
          <AssignmentsView
            assignments={myAssignments}
            confirmingId={confirmingId}
            onConfirm={handleConfirmAssignment}
            unconfirmedCount={unconfirmedAssignments}
          />
        )}

        {/* === EARNINGS VIEW === */}
        {activeView === "earnings" && (
          <EarningsView tasks={tasks} earnings={earnings} myAssignments={myAssignments} sections={sections} />
        )}

        {/* === INVOICES LOCKED VIEW === */}
        {activeView === "locked" && (
          <LockedInvoicesView lockedInvoices={lockedInvoices} sections={sections} onApproveInvoice={handleApproveInvoice} onDispute={handleDisputeInvoice} />
        )}

        {/* === INVOICES VIEW === */}
        {activeView === "invoices" && (
          <InvoicesView
            invoices={invoices}
            confirmingInvoice={confirmingInvoice}
            onApproveAll={handleApproveAll}
            onDispute={handleDispute}
            expandedInvoice={expandedInvoice}
            setExpandedInvoice={setExpandedInvoice}
            sections={sections}
          />
        )}

        {/* === PROFILE VIEW === */}
        {activeView === "profile" && (
          <ProfileView />
        )}
      </div>
    </div>
  );
};

// ========== HOME VIEW ==========
// ========== AVAILABILITY INLINE (next to name) ==========
const AvailabilityInline = () => {
  const [data, setData] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [available, setAvailable] = React.useState(true);
  const [schedule, setSchedule] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [until, setUntil] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API}/translator/availability`).then(res => {
      setData(res.data);
      setAvailable(res.data.available !== false);
      setSchedule(res.data.schedule || "");
      setReason(res.data.unavailable_reason || "");
      setUntil(res.data.unavailable_until || "");
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/translator/availability`, { available, schedule, unavailable_reason: reason, unavailable_until: until });
      toast.success(available ? "You're online" : "Availability updated");
      setEditing(false);
      setData({ available, schedule, unavailable_reason: reason, unavailable_until: until });
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const isAvail = data?.available !== false;

  if (editing) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-[100]" onClick={() => setEditing(false)} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 border border-slate-700 rounded-xl p-5 w-72 shadow-2xl z-[101]">
          <p className="text-sm font-semibold text-white mb-3">Update Availability</p>
          <div className="flex gap-2 mb-3">
            <button onClick={() => setAvailable(true)} className={`flex-1 py-2 rounded-lg text-xs font-medium ${available ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-400"}`}>Available</button>
            <button onClick={() => setAvailable(false)} className={`flex-1 py-2 rounded-lg text-xs font-medium ${!available ? "bg-red-500 text-white" : "bg-slate-700 text-slate-400"}`}>Unavailable</button>
          </div>
          {available ? (
            <input value={schedule} onChange={e => setSchedule(e.target.value)} placeholder="e.g. Mon-Fri 9am-6pm" className="w-full h-8 px-3 rounded bg-slate-700 border border-slate-600 text-xs text-white placeholder:text-slate-500 outline-none mb-3" />
          ) : (
            <><input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason..." className="w-full h-8 px-3 rounded bg-slate-700 border border-slate-600 text-xs text-white placeholder:text-slate-500 outline-none mb-2" />
            <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="w-full h-8 px-3 rounded bg-slate-700 border border-slate-600 text-xs text-white outline-none mb-3" /></>
          )}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="flex-1 h-8 bg-teal-500 text-white rounded text-xs font-medium">{saving ? "..." : "Save"}</button>
            <button onClick={() => setEditing(false)} className="flex-1 h-8 bg-slate-700 text-slate-300 rounded text-xs">Cancel</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="relative inline-flex items-center gap-2 cursor-pointer group" onClick={(e) => { e.stopPropagation(); setEditing(true); }} title="Click to update availability">
      {isAvail ? (
        <>
          <style>{`@keyframes blink-green { 0%, 100% { opacity: 1; box-shadow: 0 0 8px #10b981; } 50% { opacity: 0.4; box-shadow: 0 0 2px #10b981; } }`}</style>
          <div className="w-3.5 h-3.5 rounded-full bg-emerald-400" style={{ animation: "blink-green 1.5s ease-in-out infinite" }} />
          <span className="text-emerald-400 text-sm font-medium">available</span>
          <span className="text-slate-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity">Change status</span>
        </>
      ) : (
        <>
          <style>{`@keyframes shutdown { 0% { opacity: 0.6; } 50% { opacity: 0.15; } 100% { opacity: 0.6; } }`}</style>
          <div className="w-3.5 h-3.5 rounded-full bg-slate-600 border-2 border-slate-500" style={{ animation: "shutdown 3s ease-in-out infinite" }} />
          <span className="text-slate-500 text-sm font-medium">offline</span>
          {data?.unavailable_until && <span className="text-slate-600 text-xs">till {data.unavailable_until}</span>}
          <span className="text-slate-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity">Change</span>
        </>
      )}
      {editing}
    </div>
  );
};

// ========== AVAILABILITY WIDGET (legacy, kept for reference) ==========
const AvailabilityWidget = () => { return null; };

const HomeView = ({ earnings, tasks, myAssignments, user, navigate, pendencyStatus, sections = {}, currentMonthLocked = 0, lockedInvoices, projectsByMonth, handleUpdateTranslatorStatus }) => {
  const isOn = (key) => sections[key] !== false;
  const displayName = earnings?.display_name || user?.display_name || "Translator";
  const totalEarned = (earnings?.total_earned || 0) + (tasks?.assignment_earnings?.total_amount || 0);
  const totalTasks = tasks?.total_tasks || 0;
  const totalAssignments = myAssignments?.length || 0;
  const completedTasks = (tasks?.tasks || []).filter(t => t.task_status?.toLowerCase() === "completed").length;
  const confirmedTasks = (tasks?.tasks || []).filter(t => t.translator_confirmed).length;
  const taskTypes = [...new Set((tasks?.tasks || []).map(t => t.task || "").filter(Boolean))];
  const showAmounts = isOn("show_amounts");
  const lockedBatchCount = lockedInvoices?.locked_batches?.length || 0;
  const workEmail = earnings?.work_email || user?.work_email || "";

  // Current month projected earnings from projects-by-month
  const now = new Date();
  const currentMonthKey = `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][now.getMonth()]} ${now.getFullYear()}`;
  const currentMonthProj = (projectsByMonth?.months || []).find(m => m.month_key === currentMonthKey);
  const projectedEarnings = currentMonthProj?.total_amount || 0;

  // Last month locked earnings
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthYM = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}`;
  const lastMonthLocked = (lockedInvoices?.locked_batches || [])
    .filter(b => b.financial_month === lastMonthYM)
    .reduce((sum, b) => sum + (b.total_amount || 0), 0);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-6" data-testid="home-view">
      {/* Hero Welcome */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 p-8 md:p-10">
        <div className="absolute top-0 right-0 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-amber-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

        {/* Right side — stats grid filling full height */}
        <div className="absolute top-6 right-6 bottom-6 z-20 w-[280px]" data-testid="hero-widgets">
          {(() => {
            const today = new Date().toISOString().split("T")[0];
            const overdueCount = (tasks?.tasks || []).filter(t => {
              if (t.task_status === "Completed") return false;
              const dd = t.due_date;
              if (!dd) return false;
              const parts = String(dd).split(" ");
              if (parts.length >= 3) {
                const mo = {Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12"};
                const m = mo[parts[1]?.slice(0,3)];
                if (m) return `${parts[2]}-${m}-${parts[0].padStart(2,"0")}` < today;
              }
              return false;
            }).length;
            const projCount = new Set((tasks?.tasks || []).map(t => t.main_title).filter(Boolean)).size;
            const lifetimeEarnings = earnings?.lifetime_earned || 0;
            return (
              <div className="grid grid-cols-2 gap-3 h-full">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex flex-col justify-center">
                  <AlertCircle className="w-6 h-6 text-red-400 mb-3" />
                  <p className="text-3xl font-bold text-white">{overdueCount}</p>
                  <p className="text-[11px] text-slate-400 mt-1">Overdue</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex flex-col justify-center">
                  <FolderOpen className="w-6 h-6 text-blue-400 mb-3" />
                  <p className="text-3xl font-bold text-white">{projCount}</p>
                  <p className="text-[11px] text-slate-400 mt-1">Projects</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex flex-col justify-center col-span-2">
                  <BarChart3 className="w-6 h-6 text-emerald-400 mb-3" />
                  <p className="text-3xl font-bold text-white">{"\u20B9"}{Math.round(lifetimeEarnings).toLocaleString("en-IN")}</p>
                  <p className="text-[11px] text-slate-400 mt-1">Lifetime Earnings</p>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="relative z-10">
          <p className="text-teal-400 text-sm font-medium mb-1">{greeting},</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3 relative" style={{ fontFamily: 'Poppins, system-ui' }}>
            {displayName}
            <AvailabilityInline />
          </h1>
          <p className="text-slate-400 text-sm max-w-lg">
            Thank you for your valuable contribution to Graphikpro.<br/>Your dedication helps us deliver quality work.
          </p>
          {workEmail && (
            <p className="text-teal-400/70 text-xs mt-2" data-testid="work-email-mapping">
              You are mapped to: <span className="font-semibold text-teal-300">{workEmail}</span>
            </p>
          )}

          {/* Big Locked Amount */}
          {isOn("home_earnings_hero") && (
          <div className="mt-8 flex flex-wrap items-end gap-8 md:gap-12">
            <div>
              <p className="text-xs text-teal-400/80 uppercase tracking-widest font-semibold mb-1">Current Month Projected Earnings</p>
              <p className="text-4xl md:text-5xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, system-ui' }} data-testid="projected-earnings-hero">
                {showAmounts ? `\u20B9${projectedEarnings.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "****"}
              </p>
              {currentMonthProj && <p className="text-[10px] text-teal-400/60 mt-1">{currentMonthProj.task_count} tasks in {currentMonthKey}</p>}
            </div>
            <div className="flex gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-0.5">Last Month Locked</p>
                <p className="text-2xl font-bold text-amber-400" data-testid="last-month-locked">{showAmounts ? `\u20B9${lastMonthLocked.toLocaleString("en-IN")}` : "****"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-0.5">Tasks</p>
                <p className="text-2xl font-bold text-white">{totalTasks}</p>
                <p className="text-[10px] text-slate-500">{completedTasks} completed</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-0.5">Locked Batches</p>
                <p className="text-2xl font-bold text-teal-400">{lockedBatchCount}</p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Shared Email Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="shared-email-notice">
        <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          This email may be used by someone else. If something is not yours, please feel free to ignore it. Calculate only your working projects. This will be sorted soon.
        </p>
      </div>

      {/* ═══ LIVE STATUS PANEL ═══ */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-teal-500" />
          Your Live Status
        </h3>
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div className="text-center">
            <p className="text-xl font-bold text-blue-600">{(tasks?.tasks || []).filter(t => !t.translator_status).length}</p>
            <p className="text-[10px] text-slate-500">Needs Update</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-amber-600">{(tasks?.tasks || []).filter(t => t.translator_status === "in_progress").length}</p>
            <p className="text-[10px] text-slate-500">In Progress</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-emerald-600">{(tasks?.tasks || []).filter(t => t.translator_status === "completed").length}</p>
            <p className="text-[10px] text-slate-500">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-red-600">{(tasks?.tasks || []).filter(t => t.translator_status === "issue_flagged").length}</p>
            <p className="text-[10px] text-slate-500">Issues</p>
          </div>
        </div>
        {/* Overall progress bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks * 100) : 0}%` }} />
          </div>
          <span className="text-xs font-semibold text-slate-600">{totalTasks > 0 ? Math.round(completedTasks / totalTasks * 100) : 0}% complete</span>
        </div>
      </div>

      {/* ═══ NOTIFICATION BAR ═══ */}
      {(() => {
        const notifications = [];
        if (lockedBatchCount > 0) notifications.push({ type: "lock", text: `${lockedBatchCount} invoice batch${lockedBatchCount > 1 ? "es" : ""} locked — review & approve`, action: "/portal?view=locked", color: "amber" });
        const needsAttention = (tasks?.tasks || []).filter(t => !t.translator_status).length;
        if (needsAttention > 0) notifications.push({ type: "attention", text: `${needsAttention} tasks need your status update`, action: "/portal?view=projects", color: "blue" });
        const issues = (tasks?.tasks || []).filter(t => t.translator_status === "issue_flagged").length;
        if (issues > 0) notifications.push({ type: "issue", text: `${issues} task${issues > 1 ? "s" : ""} flagged with issues`, action: "/portal?view=projects", color: "red" });
        if (notifications.length === 0) return null;
        return (
          <div className="space-y-2">
            {notifications.map((n, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${n.color === "amber" ? "bg-amber-50 border-amber-200" : n.color === "red" ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
                {n.color === "amber" ? <Lock className="w-4 h-4 text-amber-500 shrink-0" /> : n.color === "red" ? <AlertCircle className="w-4 h-4 text-red-500 shrink-0" /> : <Clock className="w-4 h-4 text-blue-500 shrink-0" />}
                <p className={`text-xs flex-1 ${n.color === "amber" ? "text-amber-700" : n.color === "red" ? "text-red-700" : "text-blue-700"}`}>{n.text}</p>
                <button onClick={() => navigate(n.action)} className={`text-[10px] font-semibold px-2 py-1 rounded ${n.color === "amber" ? "bg-amber-100 text-amber-800" : n.color === "red" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}>
                  View <ArrowRight className="w-3 h-3 inline ml-0.5" />
                </button>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ═══ TASKS NEEDING ATTENTION ═══ */}
      {(() => {
        const needsUpdate = (tasks?.tasks || []).filter(t => !t.translator_status).slice(0, 5);
        if (needsUpdate.length === 0) return null;
        return (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Quick Status Update
              </h3>
              <button onClick={() => navigate("/portal?view=projects")} className="text-[10px] text-blue-600 font-medium hover:underline">View All</button>
            </div>
            <div className="space-y-1.5">
              {needsUpdate.map(t => (
                <div key={t.id} className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{t.main_title}{t.season_number ? ` S${t.season_number}` : ""}</p>
                    <p className="text-[10px] text-slate-400 truncate">{t.episode_title || "-"} · {t.task || "-"}</p>
                  </div>
                  <Badge className={`text-[9px] border-0 ${t.task_status === "Completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{t.task_status || "In Progress"}</Badge>
                  <TranslatorStatusSelect taskId={t.id} currentStatus={t.translator_status} currentIssueNote={t.issue_note} onUpdate={handleUpdateTranslatorStatus} dbStatus={t.task_status} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ═══ OVERDUE TASKS TIMELINE ═══ */}
      {(() => {
        const today = new Date().toISOString().split("T")[0];
        const overdue = (tasks?.tasks || []).filter(t => {
          if (t.task_status === "Completed") return false;
          const dd = formatDate(t.due_date);
          return dd && dd < today;
        }).slice(0, 5);
        if (overdue.length === 0) return null;
        return (
          <div className="bg-red-50 rounded-2xl border border-red-200 p-5">
            <h3 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Overdue Tasks ({overdue.length})
            </h3>
            <div className="space-y-1.5">
              {overdue.map(t => (
                <div key={t.id} className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-red-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{t.main_title}{t.season_number ? ` S${t.season_number}` : ""} — {t.episode_title || ""}</p>
                    <p className="text-[10px] text-red-500 font-medium">Due: {t.due_date}</p>
                  </div>
                  <Badge className="text-[9px] bg-red-100 text-red-700 border-0">{t.task || "-"}</Badge>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Database Update Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="db-update-notice">
        <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          Database is not updated live. Some projects may not show. Please check only after 25th of every month.
        </p>
      </div>

      {/* Pendency Warning */}
      {pendencyStatus?.pending_claims > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="pendency-banner">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Invoice Generation Blocked</p>
            <p className="text-xs text-red-600 mt-0.5">
              You have {pendencyStatus.pending_claims} pending missing project claim(s). Your invoice will be auto-generated once all claims are resolved and tasks are confirmed.
            </p>
            <button
              onClick={() => navigate("/portal/missing-project")}
              className="mt-2 text-xs font-medium text-red-700 underline hover:text-red-900"
            >
              View your pending claims
            </button>
          </div>
        </div>
      )}

      {/* Invoice Generated */}
      {pendencyStatus?.has_invoice && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="invoice-ready-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-800">Invoice Generated</p>
            <p className="text-xs text-emerald-600 mt-0.5">Your invoice has been auto-generated and emailed. Check the Invoices tab for details.</p>
          </div>
          <button
            onClick={() => navigate("/portal?view=invoices")}
            className="text-xs font-medium text-emerald-700 bg-emerald-100 px-3 py-1.5 rounded-lg hover:bg-emerald-200"
          >
            View Invoice
          </button>
        </div>
      )}

      {/* Quick Actions */}
      {isOn("home_quick_actions") && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button onClick={() => navigate("/portal?view=projects")} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-teal-300 hover:shadow-sm transition-all text-left group" data-testid="quick-projects">
          <FileText className="w-5 h-5 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-sm font-semibold text-slate-700">Projects Assigned</p>
          <p className="text-xs text-slate-400 mt-0.5">{totalTasks} tasks</p>
        </button>
        <button onClick={() => navigate("/portal?view=locked")} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-teal-300 hover:shadow-sm transition-all text-left group" data-testid="quick-locked">
          <Lock className="w-5 h-5 text-amber-500 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-sm font-semibold text-slate-700">Invoices Locked</p>
          <p className="text-xs text-slate-400 mt-0.5">{lockedBatchCount} batch{lockedBatchCount !== 1 ? "es" : ""}</p>
        </button>
        <button onClick={() => navigate("/portal?view=earnings")} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-teal-300 hover:shadow-sm transition-all text-left group" data-testid="quick-earnings">
          <Coins className="w-5 h-5 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-sm font-semibold text-slate-700">Total Earnings</p>
          <p className="text-xs text-slate-400 mt-0.5">{showAmounts ? `\u20B9${totalEarned.toLocaleString("en-IN")}` : "****"}</p>
        </button>
        <button onClick={() => navigate("/portal?view=analytics")} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-teal-300 hover:shadow-sm transition-all text-left group" data-testid="quick-analytics">
          <BarChart3 className="w-5 h-5 text-violet-500 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-sm font-semibold text-slate-700">Analytics</p>
          <p className="text-xs text-slate-400 mt-0.5">Performance stats</p>
        </button>
      </div>
      )}

      {/* Activity Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Task Types */}
        {isOn("home_specializations") && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-500" />
            Your Specializations
          </h3>
          <div className="flex flex-wrap gap-2">
            {taskTypes.length > 0 ? taskTypes.map((type, i) => (
              <span key={i} className="px-3 py-1.5 bg-slate-100 rounded-lg text-xs font-medium text-slate-700 border border-slate-200">
                {type}
              </span>
            )) : (
              <p className="text-xs text-slate-400">No task types assigned yet</p>
            )}
          </div>
        </div>
        )}

        {/* Earnings Split */}
        {isOn("home_earnings_split") && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Coins className="w-4 h-4 text-amber-500" />
            Earnings Split
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                <span className="text-xs text-slate-600">Task Earnings</span>
              </div>
              <span className="text-sm font-bold text-slate-700">{showAmounts ? `\u20B9${(earnings?.total_earned || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "****"}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                <span className="text-xs text-slate-600">Mini Assignment Earnings</span>
              </div>
              <span className="text-sm font-bold text-slate-700">{showAmounts ? `\u20B9${(tasks?.assignment_earnings?.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "****"}</span>
            </div>
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">Total</span>
              <span className="text-base font-extrabold text-teal-600">{showAmounts ? `\u20B9${totalEarned.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "****"}</span>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Thank you footer */}
      <div className="bg-teal-50 border border-teal-200 rounded-xl px-6 py-4 text-center">
        <p className="text-sm text-teal-800 font-medium">
          We appreciate your hard work and commitment, {displayName.split(' ')[0]}!
        </p>
        <p className="text-xs text-teal-600 mt-1">
          Your contributions make a real difference. Keep up the great work!
        </p>
      </div>
    </div>
  );
};

// ========== ANALYTICS VIEW ==========
const AnalyticsView = ({ analytics, tasks, earnings, myAssignments, sections = {} }) => {
  const isOn = (key) => sections[key] !== false;
  const showAmounts = isOn("show_amounts");
  const totalEarned = (earnings?.total_earned || 0) + (tasks?.assignment_earnings?.total_amount || 0);
  const totalTasks = tasks?.total_tasks || 0;
  const completedMin = (tasks?.tasks || []).filter(t => t.task_status?.toLowerCase() === 'completed')
    .reduce((s, t) => s + (t.duration_words || 0), 0);
  const pendingMin = (tasks?.tasks || []).filter(t => t.task_status?.toLowerCase() !== 'completed')
    .reduce((s, t) => s + (t.duration_words || 0), 0);
  const totalMin = completedMin + pendingMin;
  const progressPct = totalMin > 0 ? (completedMin / totalMin) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="analytics-view">
      {/* KPI Cards */}
      {isOn("analytics_kpi_cards") && (
      <div className="grid grid-cols-2 gap-4">
        <KpiCard icon={Coins} color="amber" label="Total Earned" value={showAmounts ? `\u20B9${totalEarned.toLocaleString('en-IN', { minimumFractionDigits: 0 })}` : "****"} sub={myAssignments.length > 0 ? `incl. \u20B9${(tasks?.assignment_earnings?.total_amount || 0).toLocaleString()} mini assigns` : null} />
        <KpiCard icon={FileText} color="blue" label="Total Tasks" value={totalTasks} sub={`${(tasks?.tasks || []).filter(t => t.task_status?.toLowerCase() === 'completed').length} completed`} />
      </div>
      )}

      {/* Progress + Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {isOn("analytics_work_progress") && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Work Progress</h3>
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20">
              <svg className="transform -rotate-90" width="80" height="80">
                <circle cx="40" cy="40" r="32" stroke="#e2e8f0" strokeWidth="8" fill="none" />
                <circle cx="40" cy="40" r="32" stroke="#14b8a6" strokeWidth="8" fill="none" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 32}`} strokeDashoffset={`${2 * Math.PI * 32 * (1 - progressPct / 100)}`}
                  className="transition-all duration-700" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-slate-700">{Math.round(progressPct)}%</span>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-slate-500"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />Completed</span>
                <span className="font-semibold text-slate-700">{Math.round(completedMin)} min</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-slate-500"><Clock className="w-3.5 h-3.5 text-amber-500" />Pending</span>
                <span className="font-semibold text-slate-700">{Math.round(pendingMin)} min</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {isOn("analytics_turnaround") && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Turnaround</h3>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-cyan-50 flex items-center justify-center">
              <Timer className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {analytics?.avg_turnaround_days > 0 ? `${analytics.avg_turnaround_days}d` : analytics?.avg_turnaround_days === 0 ? '0d' : `${Math.abs(analytics?.avg_turnaround_days || 0)}d`}
              </p>
              <p className="text-xs text-slate-400">
                {analytics?.avg_turnaround_days > 0 ? 'avg early' : analytics?.avg_turnaround_days < 0 ? 'avg late' : 'on time'}
              </p>
            </div>
          </div>
        </div>
        )}

        {isOn("analytics_invoiced_paid") && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Invoiced vs Paid</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Invoiced</span>
              <span className="font-semibold text-slate-700">{showAmounts ? `\u20B9${(earnings?.total_invoiced || 0).toLocaleString('en-IN')}` : "****"}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-emerald-600">Paid</span>
              <span className="font-semibold text-emerald-700">{showAmounts ? `\u20B9${(earnings?.total_paid || 0).toLocaleString('en-IN')}` : "****"}</span>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Charts */}
      {isOn("analytics_earnings_chart") && (
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-500" />Earnings by Project</h3>
          </div>
          <div className="p-4">
            {analytics?.earnings_by_project?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analytics.earnings_by_project} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `\u20B9${v.toLocaleString()}`} />
                  <YAxis type="category" dataKey="project" tick={{ fontSize: 9, fill: '#64748b' }} width={120} />
                  <Tooltip formatter={(v) => [`\u20B9${v.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, 'Earnings']} contentStyle={{ borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="amount" radius={[0, 4, 4, 0]} fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-xs text-slate-400 py-10">No data</p>}
          </div>
        </div>
      </div>
      )}

      {/* Task Type Distribution */}
      {isOn("analytics_task_distribution") && analytics?.task_type_distribution?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500" />Task Type Distribution</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {analytics.task_type_distribution.map((tt, i) => {
              const max = Math.max(...analytics.task_type_distribution.map(t => t.amount));
              const pct = max > 0 ? (tt.amount / max) * 100 : 0;
              const colors = ['bg-blue-500', 'bg-teal-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500'];
              return (
                <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{tt.type}</span>
                    <span className="text-[10px] text-slate-400">{tt.count} tasks</span>
                  </div>
                  <p className="text-sm font-bold text-slate-800 mb-1">{"\u20B9"}{(tt.amount || 0).toLocaleString('en-IN')}</p>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${colors[i % colors.length]} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

// ========== KPI Card ==========
const KpiCard = ({ icon: Icon, color, label, value, sub }) => {
  const bg = { amber: 'bg-amber-50 border-amber-100', blue: 'bg-blue-50 border-blue-100', emerald: 'bg-emerald-50 border-emerald-100', violet: 'bg-violet-50 border-violet-100' };
  const iconBg = { amber: 'bg-amber-100', blue: 'bg-blue-100', emerald: 'bg-emerald-100', violet: 'bg-violet-100' };
  const iconColor = { amber: 'text-amber-600', blue: 'text-blue-600', emerald: 'text-emerald-600', violet: 'text-violet-600' };
  const valueColor = { amber: 'text-amber-700', blue: 'text-blue-700', emerald: 'text-emerald-700', violet: 'text-violet-700' };
  return (
    <div className={`rounded-xl border p-4 ${bg[color]}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg[color]}`}>
          <Icon className={`w-4.5 h-4.5 ${iconColor[color]}`} />
        </div>
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{label}</p>
          <p className={`text-xl font-bold ${valueColor[color]}`}>{value}</p>
          {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

// ========== TRANSLATOR STATUS SELECT ==========
const TranslatorStatusSelect = ({ taskId, currentStatus, currentIssueNote, onUpdate, dbStatus }) => {
  const [updating, setUpdating] = React.useState(false);
  const [showIssueInput, setShowIssueInput] = React.useState(false);
  const [issueNote, setIssueNote] = React.useState(currentIssueNote || "");
  const statuses = [
    { value: "in_progress", label: "In Progress", color: "bg-blue-50 text-blue-700 border-blue-200" },
    { value: "completed", label: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { value: "issue_flagged", label: "Has Issues", color: "bg-red-50 text-red-700 border-red-200" },
  ];

  // Auto-derive status from DB if no manual translator_status
  const effectiveStatus = currentStatus || (dbStatus === "Completed" ? "verified" : null);
  const current = statuses.find(s => s.value === effectiveStatus) || null;

  const handleChange = async (val) => {
    if (val === currentStatus) return;
    if (val === "issue_flagged") {
      setShowIssueInput(true);
      return;
    }
    setUpdating(true);
    try { await onUpdate(taskId, val, null); }
    finally { setUpdating(false); }
  };

  const submitIssue = async () => {
    setUpdating(true);
    setShowIssueInput(false);
    try { await onUpdate(taskId, "issue_flagged", issueNote || "Issue reported"); }
    finally { setUpdating(false); }
  };

  if (updating) return <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 mx-auto" />;

  // Show "Verified" badge when DB says Completed and translator hasn't set anything
  if (effectiveStatus === "verified" && !currentStatus) {
    return (
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center gap-0.5 h-6 px-2 text-[10px] font-medium rounded bg-teal-50 text-teal-700 border border-teal-200">
          <Check className="w-3 h-3" />Verified
        </span>
      </div>
    );
  }

  if (showIssueInput) {
    return (
      <div className="flex items-center gap-1">
        <input type="text" value={issueNote} onChange={e => setIssueNote(e.target.value)}
          placeholder="Describe issue..."
          className="h-6 px-1.5 text-[10px] border border-red-200 rounded w-28 outline-none focus:border-red-400"
          autoFocus onKeyDown={e => e.key === "Enter" && submitIssue()} />
        <button onClick={submitIssue} className="h-6 px-1.5 bg-red-500 text-white rounded text-[9px] font-medium">Flag</button>
        <button onClick={() => setShowIssueInput(false)} className="h-6 px-1 text-slate-400 text-[10px]">x</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <select
        value={currentStatus || ""}
        onChange={e => handleChange(e.target.value)}
        className={`h-6 px-1.5 text-[10px] font-medium rounded border cursor-pointer outline-none ${current ? current.color : "bg-slate-50 text-slate-400 border-slate-200"}`}
        data-testid={`translator-status-${taskId}`}
      >
        <option value="">Set Status</option>
        {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      {currentStatus === "issue_flagged" && currentIssueNote && (
        <span className="text-[9px] text-red-500 truncate max-w-[60px]" title={currentIssueNote}>{currentIssueNote}</span>
      )}
    </div>
  );
};

// ========== PROJECTS VIEW ==========
const ProjectsView = ({ projectsByMonth, tasks, confirmingId, onStatusUpdate, pendencyStatus, navigate, sections = {} }) => {
  const [expandedMonth, setExpandedMonth] = React.useState(null);
  const [sortField, setSortField] = React.useState("due_date");
  const [sortOrder, setSortOrder] = React.useState("desc");
  const months = projectsByMonth?.months || [];
  const isOn = (key) => (sections || {})[key] !== false;
  const showAmounts = isOn("show_amounts");

  const toggleSort = (field) => {
    if (sortField === field) setSortOrder(o => o === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortOrder("desc"); }
  };

  const parseSortDate = (d) => {
    if (!d) return "0000-00-00";
    const s = String(d).trim();
    const parts = s.split(" ");
    if (parts.length >= 3) {
      const months = { Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06", Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12" };
      const mon = months[parts[1]?.slice(0, 3)];
      if (mon) return `${parts[2]}-${mon}-${parts[0].padStart(2, "0")}`;
    }
    if (s.includes("/")) { const p = s.split(",")[0].split("/"); if (p.length >= 3) { const a = +p[0], b = +p[1]; if (b > 12) return `${p[2].slice(0,4)}-${String(a).padStart(2,"0")}-${String(b).padStart(2,"0")}`; return `${p[2].slice(0,4)}-${String(a).padStart(2,"0")}-${String(b).padStart(2,"0")}`; } }
    return s.slice(0, 10);
  };

  const sortTasks = (taskList) => {
    const sorted = [...taskList];
    const dir = sortOrder === "desc" ? -1 : 1;
    sorted.sort((a, b) => {
      let av, bv;
      if (sortField === "due_date") { av = parseSortDate(a.due_date); bv = parseSortDate(b.due_date); }
      else if (sortField === "amount") { av = a.amount || 0; bv = b.amount || 0; }
      else if (sortField === "duration_words") { av = a.duration_words || 0; bv = b.duration_words || 0; }
      else if (sortField === "main_title") { av = (a.main_title || "").toLowerCase(); bv = (b.main_title || "").toLowerCase(); }
      else if (sortField === "task") { av = (a.task || "").toLowerCase(); bv = (b.task || "").toLowerCase(); }
      else if (sortField === "task_status") { av = (a.task_status || "").toLowerCase(); bv = (b.task_status || "").toLowerCase(); }
      else { av = a[sortField] || ""; bv = b[sortField] || ""; }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sorted;
  };

  const SortTH = ({ label, field, align = "left" }) => (
    <th className={`px-3 py-2 text-${align} text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-slate-800 select-none`} onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field ? <svg className={`w-3 h-3 text-blue-500 ${sortOrder === "asc" ? "" : "rotate-180"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
        : <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>}
      </span>
    </th>
  );

  // Auto-expand first month
  React.useEffect(() => {
    if (months.length > 0 && !expandedMonth) setExpandedMonth(months[0].month_key);
  }, [months]);

  return (
    <div className="space-y-4" data-testid="projects-view">
      {/* Database Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="db-update-notice-projects">
        <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          Database is not updated live. Some projects may not show. Please check only after 25th of every month.
        </p>
      </div>

      {/* Pendency Warning */}
      {pendencyStatus?.pending_claims > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700 flex-1">
            <strong>{pendencyStatus.pending_claims} pending claim(s)</strong> — Invoice auto-generation is blocked until resolved.
            <button onClick={() => navigate("/portal/missing-project")} className="ml-1 underline font-medium hover:text-amber-900">View claims</button>
          </p>
        </div>
      )}

      {pendencyStatus?.has_invoice && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <p className="text-xs text-emerald-700">Invoice has been generated and emailed to you.</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Projects by Month</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">{months.reduce((s, m) => s + m.task_count, 0)} total tasks across {months.length} months</p>
        </div>
      </div>

      {/* Month Sections */}
      <div className="space-y-3">
        {months.map((month) => {
          const isExpanded = expandedMonth === month.month_key;
          return (
            <div key={month.month_key} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`month-section-${month.sort_key}`}>
              {/* Month Header */}
              <div
                className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedMonth(isExpanded ? null : month.month_key)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{month.month_key}</p>
                    <p className="text-[10px] text-slate-400">{month.task_count} tasks</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {month.completed > 0 && (
                      <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-0.5" />{month.completed} done
                      </Badge>
                    )}
                    {month.in_progress > 0 && (
                      <Badge className="bg-amber-50 text-amber-700 border-0 text-[10px]">
                        <Clock className="w-3 h-3 mr-0.5" />{month.in_progress} in progress
                      </Badge>
                    )}
                  </div>
                  {showAmounts && (
                    <p className="text-sm font-bold text-slate-700">{"\u20B9"}{(month.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                  )}
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>

              {/* Tasks Table */}
              {isExpanded && (
                <div className="border-t border-slate-200">
                  <div>
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase w-8">#</th>
                          <SortTH label="Project" field="main_title" />
                          <SortTH label="Episode" field="episode_title" />
                          <SortTH label="Task" field="task" align="center" />
                          <SortTH label="Due Date" field="due_date" align="center" />
                          <SortTH label="Duration" field="duration_words" align="center" />
                          <SortTH label="Status" field="task_status" align="center" />
                          <SortTH label="Amount" field="amount" align="right" />
                          <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase w-32">Live Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortTasks(month.tasks).map((task, idx) => (
                          <tr key={task.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`task-row-${task.id || idx}`}>
                            <td className="px-3 py-2 text-xs text-slate-400">{idx + 1}</td>
                            <td className="px-3 py-2 text-xs font-medium text-slate-700 max-w-[160px] truncate">{task.main_title || "-"}{task.season_number ? <span className="text-[10px] text-slate-400 ml-1">S{task.season_number}</span> : ""}</td>
                            <td className="px-3 py-2 text-xs text-slate-600 max-w-[140px] truncate">{task.episode_title || task.episode_number || "-"}</td>
                            <td className="px-3 py-2 text-center">
                              <Badge className="bg-blue-50 text-blue-700 border-0 text-[10px] font-medium">{task.task || "-"}</Badge>
                              {task.is_mini_assignment && <Badge className="bg-purple-50 text-purple-600 border-0 text-[9px] ml-1">Mini</Badge>}
                            </td>
                            <td className="px-3 py-2 text-center text-[10px] text-slate-500">{task.due_date || "-"}</td>
                            <td className="px-3 py-2 text-center text-xs text-slate-600">{task.duration_words || 0}</td>
                            <td className="px-3 py-2 text-center">
                              <Badge className={`border-0 text-[10px] ${task.task_status?.toLowerCase() === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                {task.task_status || "In Progress"}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-semibold text-slate-700">
                              {"\u20B9"}{(task.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <TranslatorStatusSelect taskId={task.id} currentStatus={task.translator_status} currentIssueNote={task.issue_note} onUpdate={onStatusUpdate} dbStatus={task.task_status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {months.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">No projects assigned yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== ASSIGNMENTS VIEW ==========
const AssignmentsView = ({ assignments, confirmingId, onConfirm, unconfirmedCount }) => (
  <div className="space-y-4" data-testid="assignments-view">
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-sm font-semibold text-slate-700">Mini Assignments ({assignments.length})</h3>
        {unconfirmedCount > 0 && <p className="text-[10px] text-amber-600 mt-0.5">{unconfirmedCount} mini assignments awaiting confirmation</p>}
      </div>
      {assignments.length > 0 && (
        <p className="text-sm font-bold text-indigo-600">
          {"\u20B9"}{assignments.reduce((s, a) => s + (a.amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </p>
      )}
    </div>

    {assignments.length === 0 ? (
      <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
        <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p className="text-sm text-slate-500">No mini assignments yet</p>
        <p className="text-xs text-slate-400 mt-1">Mini assignments will appear here once created by admin</p>
      </div>
    ) : (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <ScrollArea className="h-[calc(100vh-260px)]">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Project / Episode / Task</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">PO</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Qty</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase">Rate</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase w-24">Confirm</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a, idx) => (
                <tr key={a.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`assignment-row-${a.id || idx}`}>
                  <td className="px-3 py-2.5">
                    <p className="text-xs font-medium text-slate-700">{a.main_title || ""} - {a.episode_title || ""}</p>
                    <p className="text-[10px] text-slate-400">{a.task_type}</p>
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs font-mono text-slate-500">{a.po_number}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-slate-600">{a.duration_words || a.quantity || '-'}</td>
                  <td className="px-3 py-2.5 text-right text-xs font-mono text-slate-600">
                    {a.rate_type === 'flat' ? 'Flat' : `\u20B9${(a.rate || 0).toFixed(2)}`}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold text-slate-700">
                    {"\u20B9"}{(a.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge className={`border-0 text-[10px] ${
                      a.task_status === 'Completed' ? 'bg-emerald-50 text-emerald-700' :
                      a.task_status === 'In Progress' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                    }`}>{a.task_status}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {a.translator_confirmed ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                        <Check className="w-3.5 h-3.5" /> Confirmed
                      </span>
                    ) : (
                      <Button
                        size="sm" variant="outline"
                        className="h-6 px-2 text-[10px] font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => onConfirm(a.id)}
                        disabled={confirmingId === a.id}
                        data-testid={`confirm-assign-${a.id}`}
                      >
                        {confirmingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-0.5" />Approve</>}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    )}
  </div>
);

// ========== EARNINGS VIEW ==========
const EarningsView = ({ tasks, earnings, myAssignments, sections = {} }) => {
  const isOn = (key) => sections[key] !== false;
  const showAmounts = isOn("show_amounts");
  const taskSummary = tasks?.summary || [];
  const assignSummary = {};
  (myAssignments || []).forEach(a => {
    const t = a.task_type || "Unknown";
    if (!assignSummary[t]) assignSummary[t] = { task_type: t, count: 0, total_amount: 0, total_duration: 0 };
    assignSummary[t].count += 1;
    assignSummary[t].total_amount += a.amount || 0;
    assignSummary[t].total_duration += a.duration_words || 0;
  });
  const assignList = Object.values(assignSummary);

  return (
    <div className="space-y-6" data-testid="earnings-view">
      {/* Task Earnings */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Task Earnings by Type</h3>
          <p className="text-sm font-bold text-amber-700">{showAmounts ? `\u20B9${(earnings?.total_earned || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "****"}</p>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {taskSummary.map((item, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700">{item.task_type}</p>
                  <p className="text-[10px] text-slate-400">{item.count} tasks - {item.total_duration?.toFixed(0) || 0} min</p>
                </div>
              </div>
              <p className="text-sm font-bold text-amber-700">{showAmounts ? `\u20B9${(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "****"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Assignment Earnings */}
      {assignList.length > 0 && (
        <div className="bg-white rounded-xl border border-blue-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-blue-100 bg-blue-50/50 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-blue-700">Assignment Earnings by Type</h3>
            <p className="text-sm font-bold text-blue-700">{"\u20B9"}{assignList.reduce((s, a) => s + a.total_amount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {assignList.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-blue-50/30 rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ClipboardList className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-700">{item.task_type}</p>
                    <p className="text-[10px] text-slate-400">{item.count} assignments - {item.total_duration?.toFixed(0) || 0} min</p>
                  </div>
                </div>
                <p className="text-sm font-bold text-blue-700">{"\u20B9"}{(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== LOCKED INVOICES VIEW ==========
const LockedInvoicesView = ({ lockedInvoices, sections = {}, onApproveInvoice, onDispute }) => {
  const [expandedBatch, setExpandedBatch] = React.useState(null);
  const [approvingBatch, setApprovingBatch] = React.useState(null);
  const [disputeBatch, setDisputeBatch] = React.useState(null);
  const [disputeMessage, setDisputeMessage] = React.useState("");
  const [sendingDispute, setSendingDispute] = React.useState(false);
  const isOn = (key) => (sections || {})[key] !== false;
  const showAmounts = isOn("show_amounts");
  const batches = lockedInvoices?.locked_batches || [];

  const handleDispute = async () => {
    if (!disputeBatch || !disputeMessage.trim()) return;
    setSendingDispute(true);
    try {
      await onDispute(disputeBatch, disputeMessage);
      setDisputeBatch(null);
      setDisputeMessage("");
    } finally { setSendingDispute(false); }
  };

  // Format financial month: "2026-02" -> "February 2026"
  const fmtMonth = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    const months = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${months[parseInt(m)] || m} ${y}`;
  };

  return (
    <div className="space-y-4" data-testid="locked-invoices-view">
      {/* Database Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          Database is not updated live. Some projects may not show. Please check only after 25th of every month.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Locked Invoice Batches ({batches.length})</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">These tasks are locked for invoicing by financial month</p>
        </div>
        {batches.length > 0 && showAmounts && (
          <p className="text-sm font-bold text-amber-700">
            Total: {"\u20B9"}{batches.reduce((s, b) => s + (b.total_amount || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </p>
        )}
      </div>

      {batches.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
          <Lock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">No locked invoice batches for you</p>
          <p className="text-xs text-slate-400 mt-1">Once admin locks a batch containing your tasks, it will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map((batch) => {
            const isExpanded = expandedBatch === batch.batch_id;
            return (
              <div key={batch.batch_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`locked-batch-${batch.batch_id}`}>
                {/* Batch Header */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedBatch(isExpanded ? null : batch.batch_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{batch.batch_name}</p>
                      <p className="text-[10px] text-slate-400">Financial Month: {fmtMonth(batch.financial_month)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-800" data-testid={`locked-amount-${batch.batch_id}`}>
                        {showAmounts ? `\u20B9${(batch.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "****"}
                      </p>
                      <p className="text-[10px] text-slate-400">{batch.task_count} tasks ({batch.completed} completed)</p>
                    </div>
                    {batch.invoice_approved ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>
                    ) : batch.invoice_disputed ? (
                      <Badge className="bg-red-100 text-red-700 border-0 text-[10px]"><AlertCircle className="w-3 h-3 mr-1" />Disputed</Badge>
                    ) : (
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => { setApprovingBatch(batch.batch_id); onApproveInvoice && onApproveInvoice(batch.batch_id); }}
                          disabled={approvingBatch === batch.batch_id}
                          data-testid={`approve-invoice-${batch.batch_id}`}>
                          {approvingBatch === batch.batch_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => setDisputeBatch(batch.batch_id)}
                          data-testid={`dispute-invoice-${batch.batch_id}`}>
                          <AlertCircle className="w-3 h-3 mr-1" />Dispute
                        </Button>
                      </div>
                    )}
                    <svg className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-slate-200">
                    {/* Task Type Summary */}
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                      <p className="text-[10px] text-slate-500 uppercase font-semibold mb-2">Task Breakdown</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {batch.tasks_summary.map((ts, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100">
                            <div>
                              <p className="text-xs font-medium text-slate-700">{ts.task_type}</p>
                              <p className="text-[10px] text-slate-400">{ts.count} tasks</p>
                            </div>
                            {showAmounts && <p className="text-xs font-bold text-slate-700">{"\u20B9"}{(ts.amount || 0).toLocaleString("en-IN")}</p>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tasks Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase w-8">#</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Project</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Episode</th>
                            <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Task</th>
                            <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Due Date</th>
                            <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Duration</th>
                            <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                            <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batch.tasks.map((task, idx) => (
                            <tr key={task.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                              <td className="px-3 py-2 text-xs text-slate-400">{idx + 1}</td>
                              <td className="px-3 py-2 text-xs font-medium text-slate-700 max-w-[160px] truncate">{task.main_title || "-"}{task.season_number ? <span className="text-[10px] text-slate-400 ml-1">S{task.season_number}</span> : ""}</td>
                              <td className="px-3 py-2 text-xs text-slate-600 max-w-[140px] truncate">{task.episode_title || task.episode_number || "-"}</td>
                              <td className="px-3 py-2 text-center">
                                <Badge className="bg-blue-50 text-blue-700 border-0 text-[10px]">{task.task || "-"}</Badge>
                              </td>
                              <td className="px-3 py-2 text-center text-[10px] text-slate-500">{task.due_date || "-"}</td>
                              <td className="px-3 py-2 text-center text-xs text-slate-600">{task.duration_words || 0}</td>
                              <td className="px-3 py-2 text-center">
                                <Badge className={`border-0 text-[10px] ${task.task_status?.toLowerCase() === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                  {task.task_status || "In Progress"}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-right text-xs font-semibold text-slate-700">
                                {"\u20B9"}{(task.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dispute Dialog */}
      {disputeBatch && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setDisputeBatch(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Dispute Invoice</h3>
            <p className="text-[11px] text-slate-500 mb-3">Describe what's missing or incorrect. This will be sent to the admin for review.</p>
            <textarea value={disputeMessage} onChange={e => setDisputeMessage(e.target.value)}
              rows={4} placeholder="e.g., Missing 3 tasks from project ER S12, duration for Episode 5 is incorrect..."
              className="w-full border border-slate-200 rounded-lg p-3 text-sm outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100 mb-3" />
            <div className="flex items-center gap-2">
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleDispute} disabled={sendingDispute || !disputeMessage.trim()}>
                {sendingDispute ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertCircle className="w-4 h-4 mr-1" />}
                {sendingDispute ? "Submitting..." : "Submit Dispute"}
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setDisputeBatch(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== INVOICES VIEW ==========
const InvoicesView = ({ invoices, confirmingInvoice, onApproveAll, onDispute, expandedInvoice, setExpandedInvoice, sections = {} }) => {
  const [downloadingPdf, setDownloadingPdf] = React.useState(null);
  const isOn = (key) => sections[key] !== false;
  const showAmounts = isOn("show_amounts");

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloadingPdf(invoiceId);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${(invoiceNumber || invoiceId).replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="invoices-view">
      <h3 className="text-sm font-semibold text-slate-700">My Invoices ({invoices.length})</h3>

      {invoices.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
          <Receipt className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">No invoices yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Invoice Header */}
              <div
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedInvoice(expandedInvoice === inv.id ? null : inv.id)}
                data-testid={`invoice-${inv.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Receipt className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{inv.invoice_number || inv.id}</p>
                    <p className="text-[10px] text-slate-400">{inv.line_items?.length || 0} items</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-800">{"\u20B9"}{(inv.grand_total || inv.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    <Badge className={`border-0 text-[9px] ${
                      inv.translator_confirmed ? 'bg-emerald-50 text-emerald-700' :
                      inv.translator_disputed ? 'bg-red-50 text-red-700' :
                      inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                      'bg-amber-50 text-amber-700'
                    }`}>
                      {inv.translator_confirmed ? 'Approved' : inv.translator_disputed ? 'Disputed' : inv.status || 'Pending'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedInvoice === inv.id && (
                <div className="border-t border-slate-200">
                  {/* TDS Info Banner */}
                  {isOn("invoice_tds_display") && inv.tds_applied && inv.tds_amount > 0 && (
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      <p className="text-[10px] text-amber-700">
                        <span className="font-semibold">TDS @{inv.tds_percentage}% applied</span>
                        {inv.tds_reason && <span className="ml-1">— {inv.tds_reason}</span>}
                      </p>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Item</th>
                          <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Qty</th>
                          <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Rate</th>
                          <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(inv.line_items || []).map((li, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-xs text-slate-700">{li.task_details || li.main_title || li.description || '-'}</td>
                            <td className="px-3 py-2 text-center text-xs text-slate-500">{li.duration_words || li.quantity || '-'}</td>
                            <td className="px-3 py-2 text-right text-xs font-mono text-slate-500">
                              {li.rate_type === 'flat' ? 'Flat' : `\u20B9${(li.rate || 0).toFixed(2)}`}
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-semibold text-slate-700">{"\u20B9"}{(li.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals Section */}
                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                    <div className="flex justify-end">
                      <div className="w-64 space-y-1">
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>Subtotal</span>
                          <span>{"\u20B9"}{(inv.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {isOn("invoice_tds_display") && inv.tds_amount > 0 && (
                          <div className="flex justify-between text-xs text-red-600">
                            <span>TDS @{inv.tds_percentage}%</span>
                            <span>-{"\u20B9"}{(inv.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {isOn("invoice_tds_display") && !(inv.tds_amount > 0) && (
                          <div className="flex justify-between text-[10px] text-slate-400">
                            <span>TDS</span>
                            <span>Not applicable</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-bold text-slate-800 pt-1 border-t border-slate-200">
                          <span>Total Payable</span>
                          <span className="text-teal-600">{"\u20B9"}{(inv.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="px-4 py-3 bg-white border-t border-slate-200 flex items-center gap-2 justify-between">
                    {/* Download PDF */}
                    {isOn("invoice_pdf_download") && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                      onClick={(e) => { e.stopPropagation(); handleDownloadPdf(inv.id, inv.invoice_number); }}
                      disabled={downloadingPdf === inv.id}
                      data-testid={`download-pdf-${inv.id}`}
                    >
                      {downloadingPdf === inv.id ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <FileText className="w-3 h-3 mr-1" />
                      )}
                      Download PDF
                    </Button>
                    )}

                    {/* Approve/Dispute */}
                    {!inv.translator_confirmed && !inv.translator_disputed && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-[10px] border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => onDispute(inv.id)}
                          disabled={confirmingInvoice === inv.id}
                          data-testid={`dispute-invoice-${inv.id}`}
                        >
                          Dispute
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => onApproveAll(inv.id)}
                          disabled={confirmingInvoice === inv.id}
                          data-testid={`approve-invoice-${inv.id}`}
                        >
                          {confirmingInvoice === inv.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                          Approve All
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== PROFILE VIEW ==========
const ProfileView = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/translator/profile`);
        setProfile(res.data);
        setForm({
          bank_name: res.data.bank_name || "",
          account_number: res.data.account_number || "",
          ifsc_code: res.data.ifsc_code || "",
          account_holder: res.data.account_holder || "",
          pan_number: res.data.pan_number || "",
        });
      } catch { toast.error("Failed to load profile"); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/translator/profile`, form);
      toast.success("Profile updated successfully");
      setProfile(prev => ({ ...prev, ...form }));
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save profile"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const fields = [
    { key: "pan_number", label: "PAN Number", placeholder: "ABCDE1234F" },
    { key: "bank_name", label: "Bank Name", placeholder: "e.g. State Bank of India" },
    { key: "account_number", label: "Account Number", placeholder: "Enter account number" },
    { key: "ifsc_code", label: "IFSC Code", placeholder: "e.g. SBIN0001234" },
    { key: "account_holder", label: "Account Holder Name", placeholder: "Name as per bank records" },
  ];

  return (
    <div className="max-w-2xl space-y-5" data-testid="profile-view">
      {/* Email Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-teal-500" /> Account Information
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] font-medium text-slate-400 uppercase mb-0.5">Login Email</p>
            <p className="text-slate-700 font-medium">{profile?.email || "-"}</p>
          </div>
          <div>
            <p className="text-[10px] font-medium text-slate-400 uppercase mb-0.5">Work Email</p>
            <p className="text-slate-700 font-medium">{profile?.work_email || "-"}</p>
          </div>
        </div>
      </div>

      {/* Bank & Tax Details */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Coins className="w-4 h-4 text-emerald-500" /> Bank & Tax Details
          </h3>
          {!editing ? (
            <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setEditing(true)} data-testid="profile-edit-btn">
              Edit Details
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500" onClick={() => { setEditing(false); setForm({ bank_name: profile?.bank_name || "", account_number: profile?.account_number || "", ifsc_code: profile?.ifsc_code || "", account_holder: profile?.account_holder || "", pan_number: profile?.pan_number || "" }); }} data-testid="profile-cancel-edit-btn">
              Cancel
            </Button>
          )}
        </div>

        {!editing ? (
          <div className="space-y-3">
            {fields.map(f => (
              <div key={f.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-xs text-slate-500">{f.label}</span>
                <span className="text-sm font-medium text-slate-800">{profile?.[f.key] || <span className="text-slate-300 italic">Not set</span>}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map(f => (
              <div key={f.key}>
                <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">{f.label}</label>
                <input
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                  value={form[f.key] || ""}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  data-testid={`profile-input-${f.key}`}
                />
              </div>
            ))}
            <Button onClick={handleSave} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 mt-2" data-testid="profile-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
        <p className="text-xs text-slate-500">
          Your bank and PAN details are used for invoice generation and TDS calculation. Please ensure they are accurate and up to date.
          Contact your admin if you need to update your email addresses.
        </p>
      </div>
    </div>
  );
};

// Missing Receipt icon (re-exported)
const Receipt = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M14 8H8"/><path d="M16 12H8"/><path d="M13 16H8"/>
  </svg>
);

export default TranslatorDashboard;
